import api from "./api";
import { NOTIFICATION_API_ENDPOINTS } from "../constants/notification.constant";

const BASE_URL = process.env.REACT_APP_PUSH_API_BASE || "/push";

// Get all templates
export const getTemplates = async (params) => {
  return api.get(`${BASE_URL}${NOTIFICATION_API_ENDPOINTS.GET_TEMPLATES}`, {
    params,
  });
};

// Get template by key
export const getTemplateByKey = async (key, params = {}) => {
  return api.get(
    `${BASE_URL}${NOTIFICATION_API_ENDPOINTS.GET_TEMPLATE_BY_KEY.replace(
      ":key",
      key
    )}`,
    { params }
  );
};

// Create a new template
export const createTemplate = async (templateData) => {
  return api.post(
    `${BASE_URL}${NOTIFICATION_API_ENDPOINTS.CREATE_TEMPLATE}`,
    templateData
  );
};

// Update existing template
export const updateTemplate = async (
  key,
  templateData,
  bumpVersion = false
) => {
  return api.put(
    `${BASE_URL}${NOTIFICATION_API_ENDPOINTS.GET_TEMPLATE_BY_KEY.replace(
      ":key",
      key
    )}`,
    templateData,
    {
      params: { bumpVersion },
    }
  );
};

// Save template (create or update)
export const saveTemplate = async (templateData) => {
  if (templateData._id || templateData.key) {
    // Update existing
    return updateTemplate(templateData.key, templateData, false);
  } else {
    // Create new
    return createTemplate(templateData);
  }
};

// Clone template
export const cloneTemplate = async (key, templateData, options = {}) => {
  const { locale = "en-IN" } = options;
  const url = `${BASE_URL}${NOTIFICATION_API_ENDPOINTS.CLONE_TEMPLATE.replace(
    ":key",
    key
  )}?locale=${locale}`;
  return await api.post(url, templateData);
};

// Archive template
export const archiveTemplate = async (key, version, archive) => {
  return api.delete(
    `${BASE_URL}${NOTIFICATION_API_ENDPOINTS.ARCHIVE_TEMPLATE.replace(
      ":key",
      key
    ).replace(":version", version)}`,
    {
      params: { version },
    }
  );
};

// Unarchive template
export const unarchiveTemplate = async (key, version) => {
  return api.patch(
    `${BASE_URL}${NOTIFICATION_API_ENDPOINTS.UNARCHIVE_TEMPLATE.replace(
      ":key",
      key
    ).replace(":version", version)}`,
    {
      params: { version },
    }
  );
};

// Delete template (specific version or all versions)
export const deleteTemplate = async (key, options = {}) => {
  const { version, locale = "en-IN", force = false } = options;

  const params = { locale };
  if (version) params.version = version;
  if (force) params.force = true;

  return api.delete(
    `${BASE_URL}${NOTIFICATION_API_ENDPOINTS.GET_TEMPLATE_BY_KEY.replace(
      ":key",
      key
    )}`,
    { params }
  );
};

// Validate template
export const validateTemplate = async (templateData) => {
  // Build validation payload from template data
  const payload = {
    titleTpl: templateData.titleTpl || templateData.content?.title,
    bodyTpl: templateData.bodyTpl || templateData.content?.body,
    imageUrlTpl: templateData.imageUrlTpl || templateData.content?.image,
    iconUrlTpl: templateData.iconUrlTpl || templateData.content?.icon,
    deeplinkTpl: templateData.deeplinkTpl || templateData.content?.deeplink,
    actions:
      templateData.actions ||
      templateData.content?.actions?.map((action) => ({
        labelTpl: action.labelTpl || action.label || action.title,
        deeplinkTpl: action.deeplinkTpl || action.deeplink || action.url,
      })),
    data: templateData.variables || templateData.data || {},
  };

  return api.post(
    `${BASE_URL}${NOTIFICATION_API_ENDPOINTS.VALIDATE_TEMPLATE}`,
    payload
  );
};

// Get template usage statistics
export const getTemplateUsage = async (key, period = "month") => {
  return api.get(
    `${BASE_URL}${NOTIFICATION_API_ENDPOINTS.GET_TEMPLATE_USAGE.replace(
      ":key",
      key
    )}`,
    {
      params: { period },
    }
  );
};

// Helper function to validate image URLs
export const validateImageUrl = async (url) => {
  if (!url) return true; // Empty URLs are valid

  try {
    // For template variables, consider them valid
    if (url.includes("{{") && url.includes("}}")) {
      return true;
    }

    const response = await fetch(url, {
      method: "HEAD",
      mode: "no-cors", // Avoid CORS issues during validation
    });

    // In no-cors mode, we can't read headers, so just check if request didn't fail
    return true;
  } catch (error) {
    console.warn("Image validation failed:", error);
    return false;
  }
};

// Helper function to validate action URLs
export const validateActionUrl = async (url) => {
  if (!url) return true; // Empty URLs are valid

  try {
    // For template variables, consider them valid
    if (url.includes("{{") && url.includes("}}")) {
      return true;
    }

    // Check if it's a deep link
    if (url.match(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//)) {
      return true; // Deep links are always valid
    }

    // For HTTP URLs, try to validate
    if (url.startsWith("http://") || url.startsWith("https://")) {
      const response = await fetch(url, {
        method: "HEAD",
        mode: "no-cors",
      });
      return true;
    }

    return true; // Default to valid
  } catch (error) {
    console.warn("URL validation failed:", error);
    return false;
  }
};
